import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { UserPlus, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminAgentApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedApp, setSelectedApp] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const url = filter === "all" 
        ? `${API}/agent-applications` 
        : `${API}/agent-applications?status=${filter}`;
      const response = await axios.get(url, { withCredentials: true });
      setApplications(response.data);
    } catch (error) {
      toast.error("Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    if (!confirm("Approve this application? The applicant will become an agent.")) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/agent-applications/${applicationId}/approve`, {}, { withCredentials: true });
      toast.success("Application approved! Agent created successfully.");
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/agent-applications/${selectedApp.application_id}/reject`, 
        { reason: rejectReason }, 
        { withCredentials: true }
      );
      toast.success("Application rejected");
      setRejectDialogOpen(false);
      setRejectReason("");
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const pendingCount = applications.filter(a => a.status === "pending").length;

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-applications">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif">Agent Applications</h2>
          <p className="text-muted-foreground text-sm">Review and approve applications to join your team</p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {pendingCount} Pending
          </Badge>
        )}
      </div>

      <div className="flex gap-2">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm overflow-hidden">
          {applications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No {filter === "all" ? "" : filter} applications</p>
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {applications.map((app) => (
                <div 
                  key={app.application_id} 
                  className={`p-4 cursor-pointer hover:bg-muted/50 ${selectedApp?.application_id === app.application_id ? 'bg-muted/50' : ''}`}
                  onClick={() => setSelectedApp(app)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <img 
                        src={app.photo || `https://ui-avatars.com/api/?name=${app.name}`} 
                        alt={app.name} 
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-medium">{app.name}</p>
                        <p className="text-sm text-muted-foreground">{app.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Applied: {formatDate(app.submitted_at)}
                        </p>
                      </div>
                    </div>
                    <Badge variant={
                      app.status === "pending" ? "default" : 
                      app.status === "approved" ? "secondary" : "destructive"
                    }>
                      {app.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-sm p-4">
          {selectedApp ? (
            <div className="space-y-4">
              <div className="text-center pb-4 border-b border-border">
                <img 
                  src={selectedApp.photo || `https://ui-avatars.com/api/?name=${selectedApp.name}&size=100`} 
                  alt={selectedApp.name} 
                  className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                />
                <h3 className="font-serif text-lg">{selectedApp.name}</h3>
                <Badge variant={
                  selectedApp.status === "pending" ? "default" : 
                  selectedApp.status === "approved" ? "secondary" : "destructive"
                }>
                  {selectedApp.status}
                </Badge>
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedApp.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedApp.phone}</p>
                </div>
                {selectedApp.license_number && (
                  <div>
                    <p className="text-muted-foreground">License Number</p>
                    <p className="font-medium">{selectedApp.license_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Experience</p>
                  <p className="font-medium">{selectedApp.experience_years} years</p>
                </div>
                {selectedApp.specializations?.length > 0 && (
                  <div>
                    <p className="text-muted-foreground">Specializations</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedApp.specializations.map((s, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Bio</p>
                  <p className="text-sm">{selectedApp.bio}</p>
                </div>
              </div>

              {selectedApp.status === "pending" && (
                <div className="pt-4 border-t border-border space-y-2">
                  <Button 
                    className="w-full" 
                    onClick={() => handleApprove(selectedApp.application_id)}
                    disabled={processing}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Application
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={processing}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Application
                  </Button>
                </div>
              )}

              {selectedApp.status === "rejected" && selectedApp.rejection_reason && (
                <div className="pt-4 border-t border-border">
                  <p className="text-muted-foreground text-xs">Rejection Reason:</p>
                  <p className="text-sm text-destructive">{selectedApp.rejection_reason}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select an application to view details</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to reject {selectedApp?.name}'s application?
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Reason (optional)</label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection..."
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={handleReject} disabled={processing} className="flex-1">
                {processing ? "Rejecting..." : "Confirm Reject"}
              </Button>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAgentApplications;
